import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withKeys as _withKeys, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { id: "main-content" }
const _hoisted_2 = {
  class: "modal",
  id: "modal-loading"
}
const _hoisted_3 = { id: "top-content" }
const _hoisted_4 = {
  key: 0,
  id: "left-buttons"
}
const _hoisted_5 = { id: "center-buttons" }
const _hoisted_6 = {
  key: 1,
  id: "right-buttons"
}
const _hoisted_7 = {
  id: "controls",
  class: "control-icon-wrapper"
}
const _hoisted_8 = { id: "controls-top-row" }
const _hoisted_9 = {
  key: 0,
  id: "control-checkboxes"
}
const _hoisted_10 = { class: "jl_icon_button_text" }
const _hoisted_11 = { class: "jl_icon_button_text" }
const _hoisted_12 = { class: "jl_icon_button_text" }
const _hoisted_13 = { id: "bottom-content" }
const _hoisted_14 = { id: "date-picker" }
const _hoisted_15 = { class: "video-wrapper" }
const _hoisted_16 = {
  id: "user-experience-footer",
  class: "mt-4"
}

import { storeToRefs } from "pinia";
import { v4 } from "uuid";
import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { Color, Grids, Place, Settings, WWTControl } from "@wwtelescope/engine";
import { GotoRADecZoomParams, engineStore } from "@wwtelescope/engine-pinia";
import { API_BASE_URL, BackgroundImageset, skyBackgroundImagesets, supportsTouchscreen, blurActiveElement, useWWTKeyboardControls, D2R, LocationDeg, UserExperienceRating } from "@cosmicds/vue-toolkit";
import { useDisplay} from 'vuetify';

import { throttle } from './debounce';

import { equatorialToHorizontal } from "./utils";
import { LocationRad } from "./types";
import { resetAltAzGridText, makeAltAzGridText, setupConstellationFigures, renderOneFrame } from "./wwt-hacks";

import { usePlaybackControl } from "./wwt_playback_control";
import { useTimezone } from "./timezones";

type SheetType = "nova-text" | "use-text" | "video";
type CameraParams = Omit<GotoRADecZoomParams, "instant">;
export interface MainComponentProps {
  wwtNamespace?: string;
  initialCameraParams?: CameraParams;
}

const TYCHO_ISET_NAME = "Tycho (Synthetic, Optical)";
const USNOB_ISET_NAME = "USNOB: US Naval Observatory B 1.0 (Synthetic, Optical)";

const UUID_KEY = "blaze-star-nova-uuid" as const;
const clockRate = 1000;


export default /*@__PURE__*/_defineComponent({
  __name: 'BlazeStarNova',
  props: {
    wwtNamespace: { default: "MainComponent" },
    initialCameraParams: { default: () => {
    return {
      raRad: 4.001238944138198,
      decRad: 0.5307600894728279,
      zoomDeg: 180
    };
  } }
  },
  setup(__props: any) {

const STORY_RATING_URL = `${API_BASE_URL}/blaze-star-nova/user-experience`;
const maybeUUID = window.localStorage.getItem(UUID_KEY);
const uuid = maybeUUID ?? v4();
let ratingOptedOut = window.localStorage.getItem("blaze-star-nova-rating-optout")?.toLowerCase() === "true";
const showPrivacyPolicy = ref(false);
function onOptOutClicked() {
  showRating.value = false;
  ratingOptedOut = true;
  window.localStorage.setItem("blaze-star-nova-rating-optout", "true");
}


const store = engineStore();
const { isTourPlaying } = storeToRefs(store);

useWWTKeyboardControls(store);

const playbackControl = usePlaybackControl(store, true);
// initialize playback. 
playbackControl.setSpeed(1000);
playbackControl.pause();
// destructure playbackControl
const { timePlaying } = playbackControl;

const touchscreen = supportsTouchscreen();
const { smAndDown, width, height } = useDisplay();

const aspectRatio = computed(() => width.value / height.value);

const question = Math.random() > 0.5 ? 
  "Does this spark your curiosity?" :
  "Are you learning something new?";
const currentRating = ref<UserExperienceRating | null>(null);
const currentComments = ref<string | null>(null);
const showRating = ref(false);


const props = __props;


const { smAndUp } = useDisplay();

const splash = new URLSearchParams(window.location.search).get("splash")?.toLowerCase() !== "false";
const showSplashScreen = ref(splash);
const backgroundImagesets = reactive<BackgroundImageset[]>([]);
const sheet = ref<SheetType | null>(null);
const layersLoaded = ref(false);
const positionSet = ref(false);
const accentColor = ref("#f7bb48");
const buttonColor = ref("#f7bb48");
// const tab = ref(0);
const showHorizon = ref(true);
const showAltAzGrid = ref(true);
const showControls = ref(smAndUp.value);
const showConstellations = ref(true);
const showBlazeOverlay = ref(true);
const showAlphaOverlay = ref(false);
const crbBelowHorizon = ref(true);
const _showDatePicker= ref(false);

const originalFrameRender = WWTControl.singleton.renderOneFrame.bind(WWTControl.singleton);
const boundRenderOneFrame = renderOneFrame.bind(WWTControl.singleton);
const newFrameRender = function() { 
  boundRenderOneFrame(
    showBlazeOverlay.value,
    showAlphaOverlay.value,
    showHorizon.value,
    showHorizon.value
  );
};
let beforeTourTime: Date = new Date();

// For now, we're not allowing a user to change this
const crbPlace = new Place();
crbPlace.set_RA(15 + 59 / 60 + 30.1622 / 3600);
crbPlace.set_dec(25 + 55 / 60 + 12.613 / 3600);
crbPlace.set_zoomLevel(180);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const wwtSettings: Settings = Settings.get_active();
function getWWTLocation(): LocationRad {
  return {
    longitudeRad: wwtSettings.get_locationLng() * D2R,
    latitudeRad: wwtSettings.get_locationLat() * D2R,
  };
}

async function goToTCrB(instant=false): Promise<void> {
  return store.gotoTarget({
    place: crbPlace,
    noZoom: false,
    instant,
    trackObject: false,
  });
}

function toggleAndGoToNova() {
  // const instant = isTCrBOnScreen();
  // console.log("Instant", instant);
  goToTCrB().then(() => {
    store.setBackgroundImageByName(store.backgroundImageset?.get_name() == TYCHO_ISET_NAME ? USNOB_ISET_NAME : TYCHO_ISET_NAME);
  });
}

// create selectedDate by default is today at 9pm localtime
function todayAt9pm() {
  // get's today's date and 
  // sets time time to 9pm local time
  const today = new Date();
  today.setHours(21, 0, 0, 0);
  return today;
}

const selectedDate = ref(todayAt9pm());

function updateDate(value: Date) {
  selectedDate.value = value;
}
const throttledUpdateDate = throttle(updateDate, 500);

const showLocationSelector = ref(false);

const selectedLocation = ref<LocationDeg>({
  longitudeDeg: -71.1056,
  latitudeDeg: 42.3581,
});

let href = window.location.href;
if (href.endsWith("/")) {
  href = href.slice(0, href.length - 1);
}

// useTimezone also provides selectedTimezone and browserTimezone
const { shortTimezone, selectedTimezoneOffset, browserTimezoneOffset} = useTimezone(selectedLocation);


// faking localization because
// <date-time-picker> and <time-display> are not timezone aware
const localSelectedDate = computed({
  // if you console log this date it will still say the local timezone 
  // as determined by the browser Intl.DateTimeFormat().resolvedOptions().timeZone
  // but we have manually offset it so the hours are correct for the selected timezone
  get: () => {
    const time = selectedDate.value.getTime();
    const fakeUTC = time + browserTimezoneOffset;
    return new Date(fakeUTC + selectedTimezoneOffset.value);
  },
  set: (value: Date) => {
    // get local time
    const time = value.getTime();
    // undo fake localization
    const newTime = time - selectedTimezoneOffset.value - browserTimezoneOffset;
    selectedDate.value = new Date(newTime);
  }
});


function setWWTLocation(location: LocationDeg) {
  wwtSettings.set_locationLat(location.latitudeDeg);
  wwtSettings.set_locationLng(location.longitudeDeg);
  console.log("Setting location to", location);
}

window.addEventListener("orientationchange", function() {
  adjustWWTSize(isTourPlaying.value);
});

window.addEventListener("resize", function() {
  adjustWWTSize(isTourPlaying.value);
});

onMounted(() => {
  store.waitForReady().then(async () => {
    skyBackgroundImagesets.forEach(iset => backgroundImagesets.push(iset));

    store.setBackgroundImageByName(TYCHO_ISET_NAME);

    // If there are layers to set up, do that here!
    layersLoaded.value = true;

    store.setTime(selectedDate.value);
    setWWTLocation(selectedLocation.value);

    store.setClockSync(timePlaying.value);
    store.setClockRate(clockRate);

    store.applySetting(["localHorizonMode", true]);
    store.applySetting(["showAltAzGrid", showAltAzGrid.value]);
    store.applySetting(["showAltAzGridText", showAltAzGrid.value]);
    store.applySetting(["altAzGridColor", Color.fromArgb(180, 133, 201, 254)]);
    store.applySetting(["showConstellationLabels", showConstellations.value]);
    store.applySetting(["showConstellationFigures", showConstellations.value]);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Grids._makeAltAzGridText = makeAltAzGridText;

    // We need to render one frame ahead of time
    // as there's a lot of setup done on the first frame
    // render that we need to use
    WWTControl.singleton.renderOneFrame();

    // as well as our custom text overlays
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    WWTControl.singleton.renderOneFrame = newFrameRender;
    setupConstellationFigures();

    setInterval(() => {
      if (timePlaying.value) {
        updateCrbBelowHorizon(store.currentTime);
        throttledUpdateDate(new Date(store.currentTime));
      }
    }, 10);

    // We want to make sure that the location change happens AFTER
    // the camera reposition caused by local horizon mode.
    // TODO: What is a better way to do this?
    setTimeout(() => {
      store.gotoRADecZoom({
        ...props.initialCameraParams,
        instant: true
      }).then(() => {
        positionSet.value = true;
      });
    }, 50);
  }).then(()=>{updateCrbBelowHorizon(store.currentTime);});

  // ratingDisplaySetup();
});

const ready = computed(() => layersLoaded.value && positionSet.value);

/* `isLoading` is a bit redundant here, but it could potentially have independent logic */
const isLoading = computed(() => !ready.value);

// It doesn't really matter which one we note here
const inNorthernHemisphere = computed(() => selectedLocation.value.latitudeDeg > 0);

function getCrbAlt(when: Date | null = null) {
  const location = getWWTLocation();
  const crbAltAz = equatorialToHorizontal(
    crbPlace.get_RA() * 15 * D2R,
    crbPlace.get_dec() * D2R,
    location.latitudeRad,
    location.longitudeRad,
    when ?? store.currentTime);

  return crbAltAz.altRad;
}

/* This lets us inject component data into element CSS */
const cssVars = computed(() => {
  // get the text-bottom-sheet id height and subtract it from 100vh
  return {
    "--accent-color": accentColor.value,
    "--app-content-height": (showNovaSheet.value | showUseSheet.value) ? "66%" : "100%",
  };
});


/**
  Computed flags that control whether the relevant dialogs display.
  The `sheet` data member stores which sheet is open, so these are just
  computed wrappers around modifying/querying that which can be used as
  dialog v-model values
*/
const showNovaSheet = computed({
  get() {
    return sheet.value === "nova-text";
  },
  set(_value: boolean) {
    selectSheet("nova-text");
  }
});

const showUseSheet = computed({
  get() {
    return sheet.value === "use-text";
  },
  set(_value: boolean) {
    selectSheet("use-text");
  }
});

const showVideoSheet = computed({
  get() {
    return sheet.value === "video";
  },
  set(value: boolean) {
    selectSheet("video");
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    }
  }
});

/**
  This is convenient if there's any other logic that we want to run
  when the splash screen is closed
*/
function closeSplashScreen() {
  showSplashScreen.value = false;
}

function selectSheet(sheetType: SheetType | null) {
  if (sheet.value === sheetType) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = sheetType;
  }
}

function updateCrbBelowHorizon(when: Date | null = null) {
  const alt = getCrbAlt(when);
  crbBelowHorizon.value = alt <= 0;
}

function clearCurrentTour() {
  // NB: Both of these calls are necessary
  WWTControl.singleton.stopCurrentTour();
  WWTControl.singleton.uiController = null;
}

function playPauseTour() {
  if (!isTourPlaying.value) {
    beforeTourTime = selectedDate.value;
    store.loadTour({ url: `${href}/FindingCoronaBorealis.WTT`, play: true });
  } else {
    clearCurrentTour();
    store.setBackgroundImageByName(TYCHO_ISET_NAME);
  }
}

function onTourPlayingChange(playing: boolean) {
  WWTControl.singleton.renderOneFrame = playing ? originalFrameRender : newFrameRender;
  if (playing) {
    playbackControl.pause();
  } else {
    clearCurrentTour();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    WWTControl.singleton.set__mover(null);
    store.applySetting(["localHorizonMode", true]);
    store.setBackgroundImageByName(TYCHO_ISET_NAME);
    selectedDate.value = beforeTourTime;
    // The watcher will do this, but we need it to happen now,
    // before we move
    store.setTime(beforeTourTime);
    goToTCrB(true);
  }
  adjustWWTSize(playing);
}

function adjustWWTSize(tourPlaying: boolean) {
  const wwt = document.querySelector(".wwtelescope-component");
  if (!(wwt instanceof HTMLElement)) {
    return;
  }
  const aspectRatio = window.innerWidth / window.innerHeight;
  if (aspectRatio < (4 / 3)) {
    const height = tourPlaying ? 0.75 * window.innerWidth : window.innerHeight;
    const top = tourPlaying ? 0.5 * (window.innerHeight - height) : 0;
    wwt.style.width = `${window.innerWidth}px`;
    wwt.style.height = `${height}px`;
    wwt.style.top = `${top}px`;
    wwt.style.left = "0px";
  } else {
    const width = tourPlaying ? (4 / 3) * window.innerHeight : window.innerWidth;
    const left = tourPlaying ? 0.5 * (window.innerWidth - width) : 0;
    wwt.style.height = `${window.innerHeight}px`;
    wwt.style.width = `${width}px`;
    wwt.style.left = `${left}px`;
    wwt.style.top = "0px";
  }
}

// function logWWTState() {
//   const loc = getWWTLocation();
//   const locDeg = {
//     latitudeDeg: loc.latitudeRad * 180 / Math.PI,
//     longitudeDeg: loc.longitudeRad * 180 / Math.PI,
//   };
//   console.log(getWWTLocation());
//   console.table({
//     time: store.currentTime,
//     location: locDeg,
//     selectedLocation: selectedLocation.value,
//     showHorizon: showHorizon.value,
//     showAltAzGrid: showAltAzGrid.value,
//     showConstellations: showConstellations.value,
//     crbBelowHorizon: crbBelowHorizon.value,
//   });
// }


function set9pm() {
  const time = new Date(selectedDate.value.getTime());
  time.setHours(21, 0, 0, 0);
  selectedDate.value = time;
}

function setMidnight(){
  const time = new Date(selectedDate.value.getTime());
  time.setHours(23, 59, 0, 0);
  selectedDate.value = time;
}

function toggleAlpha() {
  store.gotoRADecZoom({ raRad: 233.6719500 * D2R, decRad: 26.7146850 * D2R, zoomDeg: 180, instant: false }).then(() => {
    showAlphaOverlay.value = true;
  }).then(() => {
    setTimeout(() => {
      showAlphaOverlay.value = false;
    }, 5000);
  });
}

async function _ratingDisplaySetup() {
  if (ratingOptedOut) {
    console.log("opted out of ratings");
    return;
  }
  const existsResponse = await fetch(`${STORY_RATING_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
  });
  // NB: If we want to ask multiple questions, this logic can be adjusted
  const existsContent = await existsResponse.json();
  const exists = existsResponse.status === 200 && existsContent.ratings?.length > 0;
  if (exists) {
    return;
  }
  setTimeout(() => {
    showRating.value = true; 
  }, 40_000);
}

function updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
  const body: Record<string, unknown> = {
    uuid,
    question,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    story_name: "blaze-star-nova",
  };
  if (rating) {
    body.rating = rating;
  }
  if (comments) {
    body.comments = comments;
  }
  fetch(STORY_RATING_URL, {
    method: "PUT",
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

watch(isTourPlaying, onTourPlayingChange);

watch(showAltAzGrid, (show) => {
  store.applySetting(["showAltAzGrid", show]);
});

watch(showConstellations, (show) => {
  store.applySetting(["showConstellationLabels", show]);
  store.applySetting(["showConstellationFigures", show]);
});

watch(selectedDate, (date) => {
  // if we are playing this already getting updated
  store.setTime(date);
  updateCrbBelowHorizon(date);
  WWTControl.singleton.renderOneFrame();
});

watch(selectedLocation, (location: LocationDeg) => {
  setWWTLocation(location);
  updateCrbBelowHorizon();
  WWTControl.singleton.renderOneFrame();
});

watch(inNorthernHemisphere, (_inNorth: boolean) => resetAltAzGridText());


return (_ctx: any,_cache: any) => {
  const _component_WorldWideTelescope = _resolveComponent("WorldWideTelescope")!
  const _component_splash_screen = _resolveComponent("splash-screen")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_location_selector = _resolveComponent("location-selector")!
  const _component_geolocation_button = _resolveComponent("geolocation-button")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_time_display = _resolveComponent("time-display")!
  const _component_date_time_picker = _resolveComponent("date-time-picker")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_credit_logos = _resolveComponent("credit-logos")!
  const _component_bottom_nova_sheet = _resolveComponent("bottom-nova-sheet")!
  const _component_bottom_use_sheet = _resolveComponent("bottom-use-sheet")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_user_experience = _resolveComponent("user-experience")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_cds_privacy_policy = _resolveComponent("cds-privacy-policy")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, {
    id: "app",
    style: _normalizeStyle(cssVars.value)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_WorldWideTelescope, { "wwt-namespace": __props.wwtNamespace }, null, 8, ["wwt-namespace"]),
        _createVNode(_component_splash_screen, {
          modelValue: showSplashScreen.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showSplashScreen).value = $event)),
          onClose: closeSplashScreen,
          "css-vars": cssVars.value
        }, null, 8, ["modelValue", "css-vars"]),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_2, [...(_cache[46] || (_cache[46] = [
              _createElementVNode("div", { class: "container" }, [
                _createElementVNode("div", { class: "spinner" }),
                _createElementVNode("p", null, "Loading …")
              ], -1)
            ]))], 512), [
              [_vShow, isLoading.value]
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_3, [
          (!_unref(isTourPlaying))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_icon_button, {
                  modelValue: showNovaSheet.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showNovaSheet).value = $event)),
                  "fa-icon": "book-open",
                  color: buttonColor.value,
                  "tooltip-text": showNovaSheet.value ? 'Hide Info' : 'Learn About Novas',
                  "tooltip-location": "start"
                }, null, 8, ["modelValue", "color", "tooltip-text"]),
                _createVNode(_component_icon_button, {
                  modelValue: showUseSheet.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showUseSheet).value = $event)),
                  "fa-icon": "info",
                  color: buttonColor.value,
                  "tooltip-text": showUseSheet.value ? 'Hide Info' : 'How to Navigate',
                  "tooltip-location": "start"
                }, null, 8, ["modelValue", "color", "tooltip-text"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            (!_unref(isTourPlaying))
              ? (_openBlock(), _createBlock(_component_icon_button, {
                  key: 0,
                  modelValue: showLocationSelector.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showLocationSelector).value = $event)),
                  "fa-icon": "location-dot",
                  color: buttonColor.value,
                  "tooltip-text": "Select Location",
                  "tooltip-location": "start"
                }, null, 8, ["modelValue", "color"]))
              : _createCommentVNode("", true),
            _createVNode(_component_v_dialog, {
              modelValue: showLocationSelector.value,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showLocationSelector).value = $event)),
              "max-width": "fit-content",
              transition: "slide-y-transition",
              id: "eclipse-prediction-sheet"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, {
                      style: {"position":"absolute","right":"12px","top":"12px","cursor":"pointer","padding":"1em","margin":"-1em","z-index":"1000"},
                      icon: "xmark",
                      size: "xl",
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (showLocationSelector.value = false)),
                      onKeyup: _cache[5] || (_cache[5] = _withKeys(($event: any) => (showLocationSelector.value = false), ["enter"])),
                      tabindex: "0",
                      color: "black"
                    }),
                    _createVNode(_component_location_selector, {
                      modelValue: selectedLocation.value,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedLocation).value = $event))
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_geolocation_button, {
                      debug: false,
                      size: "30px",
                      density: "default",
                      elevation: "5",
                      color: "black",
                      onLocation: _cache[7] || (_cache[7] = ($event: any) => (selectedLocation.value = {longitudeDeg: $event.longitude, latitudeDeg: $event.latitude}))
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          (!_unref(isTourPlaying))
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_font_awesome_icon, {
                      size: "lg",
                      class: "tab-focusable",
                      icon: showControls.value ? `chevron-down` : `gear`,
                      onClick: _cache[9] || (_cache[9] = ($event: any) => (showControls.value = !showControls.value)),
                      onKeyup: _cache[10] || (_cache[10] = _withKeys(($event: any) => (showControls.value = !showControls.value), ["enter"])),
                      tabindex: "0"
                    }, null, 8, ["icon"])
                  ]),
                  (showControls.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createVNode(_component_v_checkbox, {
                          color: accentColor.value,
                          modelValue: showAltAzGrid.value,
                          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((showAltAzGrid).value = $event)),
                          onKeyup: _cache[12] || (_cache[12] = _withKeys(($event: any) => (showAltAzGrid.value = !showAltAzGrid.value), ["enter"])),
                          label: "Sky Grid",
                          "hide-details": ""
                        }, null, 8, ["color", "modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          color: accentColor.value,
                          modelValue: showHorizon.value,
                          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((showHorizon).value = $event)),
                          onKeyup: _cache[14] || (_cache[14] = _withKeys(($event: any) => (showHorizon.value = !showHorizon.value), ["enter"])),
                          label: "Horizon",
                          "hide-details": ""
                        }, null, 8, ["color", "modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          color: accentColor.value,
                          modelValue: showConstellations.value,
                          "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showConstellations).value = $event)),
                          onKeyup: _cache[16] || (_cache[16] = _withKeys(($event: any) => (showConstellations.value = !showConstellations.value), ["enter"])),
                          label: "Constellations",
                          "hide-details": ""
                        }, null, 8, ["color", "modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          color: accentColor.value,
                          modelValue: showBlazeOverlay.value,
                          "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showBlazeOverlay).value = $event)),
                          onKeyup: _cache[18] || (_cache[18] = _withKeys(($event: any) => (showBlazeOverlay.value = !showBlazeOverlay.value), ["enter"])),
                          label: "Blaze Star Location",
                          "hide-details": ""
                        }, null, 8, ["color", "modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          color: accentColor.value,
                          modelValue: showAlphaOverlay.value,
                          "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((showAlphaOverlay).value = $event)),
                          onKeyup: _cache[20] || (_cache[20] = _withKeys(($event: any) => (showAlphaOverlay.value = !showAlphaOverlay.value), ["enter"])),
                          label: "Alphecca Location",
                          "hide-details": ""
                        }, null, 8, ["color", "modelValue"])
                      ]))
                    : _createCommentVNode("", true)
                ]),
                (crbBelowHorizon.value)
                  ? (_openBlock(), _createBlock(_component_v_chip, {
                      key: 0,
                      class: "chip-text"
                    }, {
                      default: _withCtx(() => [...(_cache[47] || (_cache[47] = [
                        _createTextVNode(" Corona Borealis is Set ", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        _cache[57] || (_cache[57] = _createElementVNode("div", { id: "empty-space" }, null, -1)),
        _createElementVNode("div", {
          id: "playback-controls",
          class: _normalizeClass({'justify-md-end': _unref(isTourPlaying), 'px-4': _unref(isTourPlaying), 'pc-widescreen': aspectRatio.value > 1.5})
        }, [
          _createVNode(_component_icon_button, {
            onActivate: _cache[21] || (_cache[21] = () => playPauseTour()),
            "fa-icon": _unref(isTourPlaying) ? 'stop' : 'play',
            color: buttonColor.value,
            "tooltip-text": _unref(isTourPlaying) ? 'Stop tour' : 'Play tour',
            "tooltip-location": "top"
          }, {
            button: _withCtx(() => [
              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(isTourPlaying) ? 'Leave tour and return to main view' : 'Show me how to find the nova'), 1)
            ]),
            _: 1
          }, 8, ["fa-icon", "color", "tooltip-text"]),
          (!_unref(isTourPlaying) && !(aspectRatio.value > 1.5 && _unref(height) < 330))
            ? (_openBlock(), _createBlock(_component_icon_button, {
                key: 0,
                onActivate: _cache[22] || (_cache[22] = () => {
            toggleAndGoToNova();  
          }),
                color: buttonColor.value,
                "tooltip-text": _unref(store).backgroundImageset?.get_name() == TYCHO_ISET_NAME ? 'Show nova' : 'Hide nova',
                "tooltip-location": "top"
              }, {
                button: _withCtx(() => [
                  _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(store).backgroundImageset?.get_name() == TYCHO_ISET_NAME ?
              'Show me what the nova will look like' :
              'Show me what T CrB usually looks like'), 1)
                ]),
                _: 1
              }, 8, ["color", "tooltip-text"]))
            : _createCommentVNode("", true),
          (!_unref(isTourPlaying))
            ? (_openBlock(), _createBlock(_component_icon_button, {
                key: 1,
                onActivate: _cache[23] || (_cache[23] = () => goToTCrB()),
                "fa-icon": "star",
                color: buttonColor.value,
                "tooltip-text": "Center on T CrB",
                "tooltip-location": "top"
              }, {
                button: _withCtx(() => [
                  _createElementVNode("span", _hoisted_12, [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [...(_cache[48] || (_cache[48] = [
                        _createTextVNode("mdi-flare", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[49] || (_cache[49] = _createElementVNode("span", null, " Go to T CrB", -1))
                  ])
                ]),
                _: 1
              }, 8, ["color"]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: "icon-wrapper jl_icon-button jl_debug",
            onClick: _cache[24] || (_cache[24] = ($event: any) => (selectedDate.value = new Date())),
            style: _normalizeStyle({color: buttonColor.value})
          }, [
            _createVNode(_component_font_awesome_icon, { icon: "clock" }),
            _cache[50] || (_cache[50] = _createTextVNode(" Set time to Now", -1))
          ], 4),
          _createElementVNode("button", {
            class: "icon-wrapper jl_icon-button jl_debug",
            onClick: _cache[25] || (_cache[25] = ($event: any) => (selectedDate.value = todayAt9pm())),
            style: _normalizeStyle({color: buttonColor.value})
          }, [
            _createVNode(_component_font_awesome_icon, { icon: "clock" }),
            _cache[51] || (_cache[51] = _createTextVNode(" Set time to 9pm", -1))
          ], 4)
        ], 2),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _createVNode(_component_v_overlay, {
              activator: "parent",
              "location-strategy": "connected",
              location: "top end",
              origin: "bottom end",
              scrim: false,
              style: _normalizeStyle(cssVars.value)
            }, {
              activator: _withCtx(({props}) => [
                _createVNode(_component_v_card, _mergeProps(props, {
                  class: "td__card",
                  width: "fit-content",
                  rounded: "lg",
                  tabindex: "0",
                  onKeyup: _withKeys(props.onClick, ["enter"])
                }), {
                  default: _withCtx(() => [
                    (!_unref(isTourPlaying))
                      ? (_openBlock(), _createBlock(_component_time_display, {
                          key: 0,
                          class: "bsn__time",
                          date: localSelectedDate.value,
                          ampm: "",
                          "short-time-date": _unref(smAndDown),
                          "show-timezone": "",
                          timezone: _unref(shortTimezone)
                        }, null, 8, ["date", "short-time-date", "timezone"]))
                      : _createCommentVNode("", true),
                    (!_unref(isTourPlaying))
                      ? (_openBlock(), _createBlock(_component_v_icon, {
                          key: 1,
                          class: "td__icon"
                        }, {
                          default: _withCtx(() => [...(_cache[52] || (_cache[52] = [
                            _createTextVNode("mdi-cursor-default-click", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 16, ["onKeyup"])
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  width: "fit-content",
                  elevation: "5"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_date_time_picker, {
                      modelValue: localSelectedDate.value,
                      "onUpdate:modelValue": _cache[29] || (_cache[29] = ($event: any) => ((localSelectedDate).value = $event)),
                      "editable-time": true
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("button", {
                          class: "dtp__button",
                          onClick: _cache[26] || (_cache[26] = () => {_unref(playbackControl).pause(); set9pm(); goToTCrB()}),
                          name: "set-9pm",
                          "aria-label": "Set time to 9pm"
                        }, "9pm"),
                        _createElementVNode("button", {
                          class: "dtp__button",
                          onClick: _cache[27] || (_cache[27] = () => {_unref(playbackControl).pause(); setMidnight(); goToTCrB()}),
                          name: "set-midnight",
                          "aria-label": "Set time to Midnight"
                        }, "Midnight"),
                        _createElementVNode("button", {
                          class: "dtp__button",
                          onClick: _cache[28] || (_cache[28] = () => {_unref(playbackControl).pause(); selectedDate.value = new Date(); goToTCrB()}),
                          name: "set-now",
                          "aria-label": "Set time to Now"
                        }, "Now")
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["style"])
          ]),
          (!_unref(isTourPlaying))
            ? (_openBlock(), _createBlock(_component_icon_button, {
                key: 0,
                "fa-icon": _unref(timePlaying) ? 'pause' : 'play',
                color: buttonColor.value,
                "tooltip-text": _unref(timePlaying) ? 'Pause time' : 'Let time move forward',
                "tooltip-location": "start",
                onActivate: _cache[30] || (_cache[30] = ()=>{_unref(playbackControl).togglePlay()}),
                style: {"align-self":"center"}
              }, null, 8, ["fa-icon", "color", "tooltip-text"]))
            : _createCommentVNode("", true),
          _cache[53] || (_cache[53] = _createElementVNode("div", { style: {"flex-grow":"1"} }, null, -1)),
          (!_unref(smAndDown) && !_unref(isTourPlaying))
            ? (_openBlock(), _createBlock(_component_credit_logos, {
                key: 1,
                "default-logos": ['cosmicds', 'wwt', 'sciact', 'nasa'],
                style: {"margin":"1em"},
                "logo-size": "25px"
              }))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_component_v_dialog, {
          id: "video-container",
          modelValue: showVideoSheet.value,
          "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((showVideoSheet).value = $event)),
          transition: "slide-y-transition",
          fullscreen: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_15, [
              _createVNode(_component_font_awesome_icon, {
                id: "video-close-icon",
                class: "close-icon",
                icon: "times",
                size: "lg",
                onClick: _cache[31] || (_cache[31] = ($event: any) => (showVideoSheet.value = false)),
                onKeyup: _cache[32] || (_cache[32] = _withKeys(($event: any) => (showVideoSheet.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[54] || (_cache[54] = _createElementVNode("video", {
                controls: "",
                id: "info-video"
              }, [
                _createElementVNode("source", {
                  src: "",
                  type: "video/mp4"
                })
              ], -1))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_bottom_nova_sheet, {
          cssVars: cssVars.value,
          modelValue: showNovaSheet.value,
          "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event: any) => ((showNovaSheet).value = $event)),
          "accent-color": accentColor.value,
          touchscreen: _unref(touchscreen),
          "show-blaze-overlay": showBlazeOverlay.value,
          "show-alpha-overlay": showAlphaOverlay.value,
          onToggleBlaze: _cache[35] || (_cache[35] = () => _unref(store).gotoRADecZoom({ raRad: _unref(crbPlace).get_RA() * 15 * _unref(D2R), decRad: _unref(crbPlace).get_dec() * _unref(D2R), zoomDeg: 180, instant: false })),
          onToggleAlpha: _cache[36] || (_cache[36] = () => {
          toggleAlpha();
        }),
          "retain-focus": !showRating.value
        }, null, 8, ["cssVars", "modelValue", "accent-color", "touchscreen", "show-blaze-overlay", "show-alpha-overlay", "retain-focus"]),
        _createVNode(_component_bottom_use_sheet, {
          cssVars: cssVars.value,
          modelValue: showUseSheet.value,
          "onUpdate:modelValue": _cache[37] || (_cache[37] = ($event: any) => ((showUseSheet).value = $event)),
          "accent-color": accentColor.value,
          touchscreen: _unref(touchscreen),
          "show-blaze-overlay": showBlazeOverlay.value,
          "show-alpha-overlay": showAlphaOverlay.value,
          onToggleBlaze: _cache[38] || (_cache[38] = () => _unref(store).gotoRADecZoom({ raRad: _unref(crbPlace).get_RA() * 15 * _unref(D2R), decRad: _unref(crbPlace).get_dec() * _unref(D2R), zoomDeg: 180, instant: false })),
          onToggleAlpha: _cache[39] || (_cache[39] = () => {
          toggleAlpha();
        })
        }, null, 8, ["cssVars", "modelValue", "accent-color", "touchscreen", "show-blaze-overlay", "show-alpha-overlay"]),
        _createVNode(_component_v_container, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_expand_transition, null, {
              default: _withCtx(() => [
                _withDirectives(_createVNode(_component_user_experience, {
                  question: _unref(question),
                  "icon-size": "3x",
                  onDismiss: _cache[42] || (_cache[42] = (_rating, _comments) => {
              showRating.value = false;
            }),
                  onRating: _cache[43] || (_cache[43] = (rating) => {
              currentRating.value = rating;
              updateUserExperienceInfo(currentRating.value, currentComments.value);
            }),
                  onFinish: _cache[44] || (_cache[44] = (rating, comments) => {
              currentRating.value = rating;
              currentComments.value = comments;
              updateUserExperienceInfo(currentRating.value, currentComments.value)
              showRating.value = false;
            })
                }, {
                  footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(_component_v_btn, {
                        class: "rating-opt-put",
                        color: "#BDBDBD",
                        size: "small",
                        variant: "text",
                        onClick: onOptOutClicked
                      }, {
                        default: _withCtx(() => [...(_cache[55] || (_cache[55] = [
                          _createTextVNode(" Don't show again ", -1)
                        ]))]),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        class: "privacy-button",
                        color: "#BDBDBD",
                        size: "small",
                        onClick: _cache[40] || (_cache[40] = ($event: any) => (showPrivacyPolicy.value = true)),
                        onKeyup: _cache[41] || (_cache[41] = _withKeys(($event: any) => (showPrivacyPolicy.value = true), ["enter"])),
                        target: "_blank",
                        rel: "noopener noreferrer"
                      }, {
                        default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                          _createTextVNode(" What is this? ", -1)
                        ]))]),
                        _: 1
                      })
                    ])
                  ]),
                  _: 1
                }, 8, ["question"]), [
                  [_vShow, showRating.value]
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_cds_privacy_policy, {
              modelValue: showPrivacyPolicy.value,
              "onUpdate:modelValue": _cache[45] || (_cache[45] = ($event: any) => ((showPrivacyPolicy).value = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})